import React, { MutableRefObject } from 'react'
import styled from 'styled-components'
import { Link, TableColumn, ScreenReaderOnly } from '@ally/metronome-ui'
import { Cell } from 'react-table'
import { asCurrency } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import { formatPercentage, getDashDash } from '../../../utils'
import WealthDrawerButton from './WealthDrawerButton'
import { DashboardView } from '../../../CustomizeDashboard/types'
import { STALE_DATA_COPY } from '../../../constants'
import { TextBodyOverride } from '../../Shared'

const StyledWrapper = styled.div`
  ${({ theme: { media } }): string =>
    `
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 0;
    min-width: 100%;

  ${media.up('xl')} {
    display: inline;
  }
`};
`

const StyledLink = styled(Link)`
  ${({ theme: { colors } }): string =>
    `
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
    vertical-align: middle;
    display: inline-block;

    @media print {
      display: inline;
      color: ${colors['slate-5']};
    }
  `};
`

export interface WealthTableData {
  accounts: Account[]
  totalValue: number
  netYield?: number | null
  netReturn?: number | null
}

interface CellProps {
  cell: Cell<WealthTableData>
  isDrawerOpen: boolean
  toggleDrawer: () => void
  drawerTriggerRef: MutableRefObject<HTMLButtonElement | null>
}

interface FooterProps {
  data: WealthTableData[]
}

interface GetColumnProps {
  hasOnlyClosedAccounts: boolean
  isLgDown: boolean
  isWealthPartialOutage: boolean
  view: DashboardView
  hasStaleData: boolean
  isPersonalAdvisorEnabled: boolean
}

export const getColumns = ({
  hasOnlyClosedAccounts,
  isLgDown,
  isWealthPartialOutage,
  view,
  hasStaleData,
  isPersonalAdvisorEnabled,
}: GetColumnProps): TableColumn<WealthTableData>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const closedText = (
    <TextBodyOverride tag="span" size="sm" color="error">
      CLOSED
    </TextBodyOverride>
  )

  const headerCopy = isPersonalAdvisorEnabled
    ? 'Personal Advice'
    : 'Wealth Management'

  if (hasOnlyClosedAccounts) {
    const columns = {
      overview: {
        Header: headerCopy,
        alignment: 'left',
        isSortable: true,
        Cell: (): React.ReactNode => {
          return (
            <StyledWrapper>
              <StyledLink
                to="/ext-wealth/wealth-overview"
                text="Wealth Overview"
              />
            </StyledWrapper>
          )
        },
      },
      accountStatus: {
        Header: <ScreenReaderOnly>Account Status</ScreenReaderOnly>,
        accessor: 'status',
        alignment: isDefaultView ? 'left' : 'right',
        isSortable: true,
        Cell: (): React.ReactNode | string => closedText,
      },
      empty: {
        Header: '',
        accessor: 'empty',
      },
      empty2: {
        Header: '',
        accessor: 'empty2',
      },
    }

    const tileViewCols = [
      columns.overview,
      columns.accountStatus,
      columns.empty,
      columns.empty2,
    ] as TableColumn<WealthTableData>[]
    const defaultViewCols = [
      columns.overview,
      columns.accountStatus,
    ] as TableColumn<WealthTableData>[]

    return isDefaultView ? defaultViewCols : tileViewCols
  }

  const columns = {
    overview: {
      Header: headerCopy,
      Footer: 'Total',
      alignment: 'left',
      isSortable: true,
      Cell: (): React.ReactNode => {
        return (
          <StyledWrapper>
            <StyledLink
              to="/ext-wealth/wealth-overview"
              text="Wealth Overview"
              allytmln="wealth-wealthOverview"
            />
          </StyledWrapper>
        )
      },
    },
    value: {
      Header: 'Account Value',
      alignment: 'right',
      isSortable: true,
      Cell: ({
        cell: { row },
        isDrawerOpen,
        toggleDrawer,
        drawerTriggerRef,
      }: CellProps): React.ReactNode | string => {
        return hasStaleData ? (
          getDashDash(STALE_DATA_COPY)
        ) : (
          <WealthDrawerButton
            data={row.original}
            isDrawerOpen={isDrawerOpen}
            toggleDrawer={toggleDrawer}
            drawerTriggerRef={drawerTriggerRef}
          />
        )
      },
      Footer: ({ data }: FooterProps): React.ReactNode => {
        if (hasStaleData) {
          return getDashDash(STALE_DATA_COPY)
        }
        return asCurrency(data[0].totalValue)
      },
    },
    netReturn: {
      Header: 'Net Return %',
      accessor: 'netReturn',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value } }: CellProps): React.ReactNode | string =>
        isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(value),
      Footer: ({ data }: FooterProps): React.ReactNode | string =>
        isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(data[0].netReturn),
    },
    netYield: {
      Header: 'Yield %',
      accessor: 'netYield',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value } }: CellProps): React.ReactNode | string =>
        isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(value),
      Footer: ({ data }: FooterProps): React.ReactNode =>
        isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(data[0].netYield),
    },
    actions: {
      Header: '',
      accessor: 'actions',
      alignment: 'right',
      isSortable: true,
      Cell: (): React.ReactNode | string => '',
    },
  }

  const tileViewCols = [
    columns.overview,
    columns.value,
    columns.netReturn,
    columns.actions,
  ] as TableColumn<WealthTableData>[]
  const defaultViewCols = [
    columns.overview,
    columns.value,
    columns.netReturn,
    columns.netYield,
    columns.actions,
  ] as TableColumn<WealthTableData>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
